import type { ReactNode } from "react";

import type { DoodleBadgeId } from "@/shared/doodles/badges.data";

import { DoodleBadge } from "@/shared/doodles/DoodleBadge";

/*
 * Something on this bill waits for the person holding the till, kept at the
 * top of the order panel (Trioty restaurant canvas, "Table panel · guest
 * requests"). Two senders, two colours, so the cashier can tell at a glance
 * who is waiting: amber for a diner's QR request (accept or reject), blue for
 * a waiter's "Send to cashier" hand-over (D120: send it to the kitchen), green
 * for a diner paying or asking to pay (D44/D133: money coming in). `settled`
 * is any of them once answered: quiet, kept as a line of history.
 */
export type RequestCardTone = "guest" | "waiter" | "payment" | "settled";

const TONES: Record<RequestCardTone, { ring: string; head: string }> = {
  guest: {
    ring: "border-[color:var(--warning)]",
    head: "bg-[color:var(--warning-100)] text-[color:var(--warning-700,var(--text))]",
  },
  waiter: {
    ring: "border-[color:var(--info-fg)]",
    head: "bg-[color:var(--info-bg)] text-[color:var(--info-fg)]",
  },
  payment: {
    ring: "border-[color:var(--success)]",
    head: "bg-[color:var(--success-100)] text-[color:var(--text)]",
  },
  settled: {
    ring: "border-[color:var(--border-light)]",
    head: "bg-[color:var(--surface-muted,var(--surface))] text-[color:var(--text)]",
  },
};

export function RequestCard({
  tone,
  badge,
  title,
  subtitle,
  aside,
  children,
  footer,
  testId,
}: {
  tone: RequestCardTone;
  badge: DoodleBadgeId;
  title: ReactNode;
  subtitle?: ReactNode;
  /** The short stamp at the head's end ("since 22:31"). */
  aside?: ReactNode;
  /** The rows: one per plate asked for. Omitted when the rows live elsewhere. */
  children?: ReactNode;
  footer?: ReactNode;
  testId?: string;
}) {
  const colours = TONES[tone];
  return (
    <section
      data-ui="request-card"
      data-tone={tone}
      data-testid={testId}
      className={[
        "mb-2.5 overflow-hidden rounded-[var(--radius-card)] border-[1.5px] bg-[color:var(--surface)] shadow-[var(--shadow-card)]",
        colours.ring,
      ].join(" ")}
    >
      <header
        data-ui="request-card-head"
        className={["flex items-center gap-2.5 px-3.5 py-3", colours.head].join(" ")}
      >
        <DoodleBadge id={badge} size={28} />
        <span className="flex min-w-0 flex-1 flex-col">
          <h2 className="text-[15px] font-semibold leading-snug">{title}</h2>
          {subtitle ? (
            <span className="text-xs opacity-85">{subtitle}</span>
          ) : null}
        </span>
        {aside ? (
          <span className="shrink-0 whitespace-nowrap text-xs font-semibold tabular-nums">
            {aside}
          </span>
        ) : null}
      </header>
      {children ? (
        <ul data-ui="request-card-rows" className="flex flex-col">
          {children}
        </ul>
      ) : null}
      {footer ? (
        <div data-ui="request-card-foot" className="flex flex-col gap-2.5 px-3.5 py-3">
          {footer}
        </div>
      ) : null}
    </section>
  );
}

/** One plate asked for: what it is on the left, its price and actions at the end. */
export function RequestCardRow({
  children,
  price,
  actions,
}: {
  children: ReactNode;
  price?: ReactNode;
  actions?: ReactNode;
}) {
  return (
    <li
      data-ui="request-card-row"
      className="flex items-start gap-2.5 border-b border-[color:var(--border-light)] px-3.5 py-3"
    >
      <span className="flex min-w-0 flex-1 flex-col gap-0.5">{children}</span>
      <span className="flex shrink-0 flex-col items-end gap-2">
        {price != null ? (
          <span className="whitespace-nowrap text-sm tabular-nums">{price}</span>
        ) : null}
        {actions}
      </span>
    </li>
  );
}
